<script lang="ts">
  import { onMount, onDestroy } from "svelte"
  import { custom_event, get_current_component } from "svelte/internal"
  import { portal } from "../../helpers/svelte-portal"
  import { BROWSER } from "esm-env"

  export function createEventDispatcher() {
    const component = get_current_component()

    return (type: any, detail: any) => {
      const callbacks = component.$$.callbacks[type]

      if (callbacks) {
        const arr: any = []
        const hasCallbacks = !!callbacks.length
        const event = custom_event(type, detail)
        callbacks.slice().forEach((fn: any) => {
          const res = fn.call(component, event)
          if (res instanceof Promise) {
            arr.push(res)
          }
        })
        return Promise.all(arr).then((result) => {
          return hasCallbacks
            ? result.every((r) => r !== false)
            : event.defaultPrevented
        })
      }
      return new Promise((resolve) => resolve(false))
    }
  }

  const dispatch = createEventDispatcher()

  export let open: boolean = false
  export let align: "top" | "center" = "center"
  export let showClose: boolean = true
  export let padding: boolean = true
  export let sticky: boolean = false
  export let as: string = "div"
  export let element: HTMLDivElement | null = null
  export let contentLoading: boolean = false
  export let closeOnSuccess: boolean = true

  let prevBodyPosition: any
  let prevBodyOverflow: any
  let prevBodyWidth: any
  let outerClickTarget: any
  let loading: boolean = false
  let content: HTMLDivElement

  const sizes = ["xs", "sm", "md", "lg", "xl"]
  const classesSplit = $$props.class?.split(" ") || []
  const size = sizes.filter((a) => classesSplit.includes(a)).length
    ? sizes.filter((a) => classesSplit.includes(a))[0]
    : "none"

  const closeFn = (callbacks = {}) => {
    if (BROWSER) enableScroll()
    open = false
    loading = false
    dispatch("close", { detail: { callbacks } })
  }

  const loadingFn = (state: boolean) => {
    if (state === true) {
      loading = true
    } else {
      loading = false
    }
  }

  const openFn = (callbacks = {}) => {
    if (BROWSER) disableScroll()
    open = true
    dispatch("open", { detail: { callbacks } })
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && sticky === false && open) {
      event.preventDefault()
      closeFn()
    }
  }

  const handleOuterMousedown = (event: MouseEvent) => {
    if (event.target === element && open) {
      outerClickTarget = event.target
    }
  }

  const handleOuterMouseup = (event: MouseEvent) => {
    if (open && event.target === outerClickTarget && sticky === false) {
      event.preventDefault()
      closeFn()
    }
  }

  const submitDialog = (event: SubmitEvent) => {
    loading = true
    const formData = event.target
      ? new FormData(event.target as HTMLFormElement)
      : new FormData()
    dispatch("submit", { event, formData }).then((resolve) => {
      loading = false
      if (resolve && closeOnSuccess) {
        closeFn()
      }
    })
  }

  const disableScroll = () => {
    scrollY = window.scrollY
    prevBodyPosition = document.body.style.position
    prevBodyOverflow = document.body.style.overflow
    prevBodyWidth = document.body.style.width
    document.body.style.position = "fixed"
    document.body.style.top = `-${scrollY}px`
    document.body.style.overflow = "hidden"
    document.body.style.width = "100%"
  }

  const enableScroll = () => {
    document.body.style.position = prevBodyPosition || ""
    document.body.style.top = ""
    document.body.style.overflow = prevBodyOverflow || ""
    document.body.style.width = prevBodyWidth || ""
    window.scrollTo({
      top: scrollY,
      left: 0,
    })
  }

  onDestroy(() => {
    closeFn()
  })

  onMount(() => {
    if (open) {
      openFn()
    }
  })
</script>

<svelte:window
  on:keydown={handleKeydown}
  on:mousedown={handleOuterMousedown}
  on:mouseup={handleOuterMouseup}
/>

{#if open}
  <div
    class:justify-start={align === "top"}
    class:justify-center={align === "center"}
    style="background-color: rgba(30, 30, 30, 0.75); z-index: 45"
    class="fixed top-0 bottom-0 left-0 right-0 flex flex-col items-center w-screen h-screen py-6 lg:py-12"
    use:portal={"body"}
    bind:this={element}
  >
    {#if contentLoading}
      <div class="absolute text-white loader thickest size-6" />
    {:else}
      <svelte:element
        this={as}
        role="dialog"
        style={$$props.style || ""}
        aria-modal="true"
        bind:this={content}
        class:w-[19rem]={size === "xs"}
        class:w-[28rem]={size === "sm"}
        class:w-[34rem]={size === "md"}
        class:w-[42rem]={size === "lg"}
        class:w-[62rem]={size === "xl"}
        class:p-7={padding}
        class:gap-4={padding}
        on:submit|preventDefault={submitDialog}
        class="is-dialog flex max-h-[calc(100dvh-3rem)] lg:max-h-[calc(100dvh-6rem)] max-w-[calc(100vw-3rem)] flex-col overflow-hidden overflow-y-auto {(
          $$props.class || ''
        ).indexOf('bg-') === -1
          ? 'bg-gray-50'
          : ''} {$$props.class} text-left align-middle shadow-md shadow-gray-700 relative rounded-lg"
      >
        {#if showClose && sticky === false}
          <button
            on:click|preventDefault={closeFn}
            type="button"
            class="absolute flex items-center justify-center rounded-sm size-8 close opacity-70 hover:opacity-100 top-2 right-2 focus:ring-1 focus:ring-red-700 hover:text-red-700"
          >
            ✕
          </button>
        {/if}
        {#if $$slots.title}
          <div class="text-lg font-semibold">
            <slot
              {loading}
              {open}
              load={loadingFn}
              close={closeFn}
              name="title"
            />
          </div>
        {/if}
        {#if padding === false}
          <slot {loading} {open} load={loadingFn} close={closeFn} name="body" />
        {:else}
          <div class="text-sm">
            <slot
              {loading}
              {open}
              load={loadingFn}
              close={closeFn}
              name="body"
            />
          </div>
        {/if}
        {#if $$slots.footer}
          <div class="flex flex-row gap-3">
            <slot
              name="footer"
              {loading}
              {open}
              load={loadingFn}
              close={closeFn}
            />
          </div>
        {/if}
      </svelte:element>
    {/if}
  </div>
{/if}
<slot {loading} load={loadingFn} close={() => (open = false)} />
